import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Star, TrendingUp, Youtube, Users, ArrowRight, LogIn, LogOut, MapPin, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GooglePlacesAutocomplete from '../components/GooglePlacesAutocomplete';
import SearchLoadingState from '../components/SearchLoadingState';
import QualificationAssistant from '../components/QualificationAssistant';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { user, token, isLoggedIn, setShowLoginModal, logout } = useAuth();
  const locationState = useLocation();
  
  const [venueName, setVenueName] = useState('');
  const [location, setLocation] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trendingVenues, setTrendingVenues] = useState([]);
  const [useGoogleAutocomplete, setUseGoogleAutocomplete] = useState(true);
  const [showQualification, setShowQualification] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTrendingVenues();
    // Check if we need to search a venue from navigation state
    if (locationState.state?.searchVenue) {
      setVenueName(locationState.state.searchVenue);
      setUseGoogleAutocomplete(false);
    }
  }, [locationState.state]);

  const fetchTrendingVenues = async () => {
    try {
      const response = await axios.get(`${API_URL}/venues/trending`);
      setTrendingVenues(response.data || []);
    } catch (error) {
      console.log('No trending venues yet');
    }
  };

  // Handle Google Place selection
  const handlePlaceSelect = (placeDetails) => {
    if (placeDetails) {
      setSelectedPlace(placeDetails);
      setVenueName(placeDetails.name);
      setLocation(placeDetails.city || '');
    } else {
      setSelectedPlace(null);
      setVenueName('');
      setLocation('');
    }
  };

  // Handle manual entry (when venue not found in Google)
  const handleManualEntry = (text) => {
    setVenueName(text);
    setSelectedPlace(null);
    setUseGoogleAutocomplete(false);
    toast.info('Venue not found in Google. You can still search with manual entry.');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // No mandatory login - allow anyone to search
    performSearch();
  };

  const performSearch = async () => {
    if (!venueName.trim()) {
      toast.error('Please enter a venue name');
      return;
    }

    setIsLoading(true);
    
    try {
      // Get visitor tracking data
      const visitorId = localStorage.getItem('wvc_visitor_id') || (() => {
        const id = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('wvc_visitor_id', id);
        return id;
      })();
      
      const isNewVisitor = !localStorage.getItem('wvc_first_search_done');
      const sessionId = sessionStorage.getItem('wvc_session_id') || `sess_${Date.now()}`;
      
      // Detect device type
      const deviceType = window.innerWidth <= 768 ? 'mobile' : window.innerWidth <= 1024 ? 'tablet' : 'desktop';
      
      // Get referrer source
      const referrer = document.referrer.toLowerCase();
      let referrerSource = 'direct';
      if (referrer.includes('google.') || referrer.includes('bing.')) referrerSource = 'search';
      else if (referrer.includes('facebook.') || referrer.includes('twitter.') || referrer.includes('instagram.')) referrerSource = 'social';
      else if (referrer && !referrer.includes(window.location.hostname)) referrerSource = 'referral';
      
      const searchPayload = {
        venue_name: venueName.trim(),
        location: location.trim() || null,
        session_id: sessionId,
        visitor_id: visitorId,
        is_new_visitor: isNewVisitor,
        device_type: deviceType,
        referrer_source: referrerSource,
        page_context: 'home',
        visitor_city: localStorage.getItem('wvc_visitor_city') || null,
        visitor_country: localStorage.getItem('wvc_visitor_country') || null
      };

      // If we have a selected place, include the place_id for better accuracy
      if (selectedPlace?.place_id) {
        searchPayload.place_id = selectedPlace.place_id;
      }

      // Include token if logged in, but don't require it
      const tokenParam = token ? `?token=${token}` : '';
      const response = await axios.post(`${API_URL}/venue/search${tokenParam}`, searchPayload);

      if (response.data) {
        // Mark that user has done a search
        localStorage.setItem('wvc_first_search_done', 'true');
        navigate('/report', { state: { report: response.data, selectedPlace } });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Star,
      title: 'Trust Score',
      description: 'AI-analyzed sentiment from real reviews'
    },
    {
      icon: Star,
      title: 'Staff Highlights',
      description: 'Know who to expect at your event'
    },
    {
      icon: TrendingUp,
      title: 'Red Flag Alerts',
      description: 'Hidden concerns exposed'
    },
    {
      icon: Youtube,
      title: 'Video Validation',
      description: 'Curated YouTube tours & reviews'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8] noise-bg">
      {/* Loading State */}
      {isLoading && <SearchLoadingState venueName={venueName} />}
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-stone-900 rounded-full flex items-center justify-center">
                <span className="text-white font-serif text-sm font-bold">W</span>
              </div>
              <span className="font-serif text-xl font-medium text-stone-900">WVC</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-stone-500 hover:text-stone-900 text-sm font-medium transition-colors">Features</a>
              <a href="#how-it-works" className="text-stone-500 hover:text-stone-900 text-sm font-medium transition-colors">How it Works</a>
              <a href="/venue-visit-reports" className="text-stone-500 hover:text-stone-900 text-sm font-medium transition-colors flex items-center gap-1">
                <Star className="w-4 h-4" />
                Venue Visit Report
              </a>
              <a href="/community" className="text-stone-500 hover:text-stone-900 text-sm font-medium transition-colors flex items-center gap-1">
                <Users className="w-4 h-4" />
                Community
              </a>
              {user ? (
                <a href="/shortlist" className="text-stone-500 hover:text-stone-900 text-sm font-medium transition-colors">My Shortlist</a>
              ) : null}
            </nav>
            <div className="flex items-center gap-3">
              {user ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-stone-600 hidden sm:block">{user.name}</span>
                  <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setShowLoginModal(true)} className="bg-stone-900 hover:bg-stone-800" data-testid="login-btn">
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Brand Badge - Simple text branding */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full shadow-sm mb-8 animate-fade-in">
            <span className="text-base font-medium text-stone-700">Wedding Venues Company</span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-stone-900 tracking-tight mb-6 animate-fade-in delay-100">
            India&apos;s Trust-First Platform for <br />
            <span className="italic">Wedding Venue Research</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto mb-8 animate-fade-in delay-200 leading-relaxed">
            Skip hours of research. Get venue scores for the place you&apos;re planning to book, AI insights from real unfiltered reviews, staff highlights, all in one place, in under 60 seconds.
          </p>

          {/* Search Prompt */}
          <p className="text-base text-stone-600 mb-4 animate-fade-in delay-250">
            Type a venue name to see reviews, scores, and videos.
          </p>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto animate-fade-in delay-300">
            <div className="flex flex-col gap-3">
              {/* Google Places Autocomplete - Main Search */}
              {useGoogleAutocomplete ? (
                <GooglePlacesAutocomplete
                  onPlaceSelect={handlePlaceSelect}
                  onManualEntry={handleManualEntry}
                  placeholder="Search venue by name or location..."
                  showSelectedDetails={false}
                  allowManualEntry={true}
                  className="flex-1"
                />
              ) : (
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 z-10" />
                  <Input
                    data-testid="venue-search-input"
                    type="text"
                    placeholder="Enter venue name..."
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    className="h-14 pl-12 pr-4 rounded-full border-stone-200 bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all text-lg shadow-sm w-full"
                  />
                </div>
              )}
              
              {/* Selected venue info */}
              {selectedPlace && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-left flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">
                      <strong>{selectedPlace.name}</strong>
                      {selectedPlace.city && ` • ${selectedPlace.city}`}
                      {selectedPlace.rating && ` • ⭐ ${selectedPlace.rating}`}
                    </span>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Verified</span>
                </div>
              )}
              
              {/* Manual entry warning */}
              {!useGoogleAutocomplete && venueName && !selectedPlace && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-left flex items-center gap-2">
                  <span className="text-xs text-amber-700">Manual search mode - results may be less accurate</span>
                  <button 
                    type="button" 
                    onClick={() => { setUseGoogleAutocomplete(true); setVenueName(''); }}
                    className="text-xs text-amber-600 underline hover:text-amber-800"
                  >
                    Switch to Google Search
                  </button>
                </div>
              )}

              {/* Search Button */}
              <Button
                data-testid="search-submit-btn"
                type="submit"
                disabled={isLoading}
                className="btn-primary h-14 px-8 flex items-center gap-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Get Report
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Most Searched Venues Section */}
        {trendingVenues.length > 0 && (
          <section className="max-w-6xl mx-auto mt-16">
            <h2 className="font-serif text-2xl font-medium text-stone-900 mb-6 text-center">Most Searched Venues</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingVenues.slice(0, 6).map((venue, idx) => (
                <button 
                  key={idx} 
                  onClick={() => {
                    // No login required - allow anyone to select a venue
                    setVenueName(venue.name);
                    setLocation(venue.location || '');
                    setUseGoogleAutocomplete(false);
                  }}
                  className="bg-white border border-stone-200 rounded-xl p-4 hover:shadow-md hover:border-stone-300 transition-all text-left cursor-pointer"
                  data-testid={`most-searched-venue-${idx}`}
                >
                  <h3 className="font-medium text-stone-900">{venue.name}</h3>
                  {venue.location && <p className="text-sm text-stone-500">{venue.location}</p>}
                  <p className="text-xs text-amber-600 mt-2">Searched {venue.search_count} times</p>
                </button>
              ))}
            </div>
            {trendingVenues.length > 6 && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => navigate('/trending')}
                  variant="outline"
                  className="border-stone-300 hover:bg-stone-50"
                  data-testid="view-all-trending-btn"
                >
                  View All Most Searched Venues
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </section>
        )}

        {/* About WVC Section */}
        <section className="max-w-4xl mx-auto mt-20 bg-white border border-stone-200 rounded-2xl p-8">
          <h2 className="font-serif text-2xl font-medium text-stone-900 mb-4 text-center">About Wedding Venues Company</h2>
          <p className="text-center text-stone-700 font-medium mb-6 italic">
            "We don't recommend venues. We help you research them better before you decide."
          </p>
          <div className="text-stone-600 space-y-4 text-center">
            <p>
              Wedding Venues Company (WVC) is India&apos;s trust-first platform for wedding venue research. We believe that finding the perfect venue shouldn&apos;t require weeks of research, unreliable referrals, or hidden surprises.
            </p>
            <p>
              Our AI-powered platform analyzes real Google reviews, venue websites, and YouTube videos to generate comprehensive Venue Intelligence Reports. Every score is transparent, every insight is data-backed, and every report prioritizes your trust over vendor interests.
            </p>
            <p className="text-sm text-stone-500">
              No paid placements. No hidden boosts. Just honest, data-driven insights to help you make the most informed decision for your special day.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="max-w-6xl mx-auto mt-32">
          <div className="text-center mb-16">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-stone-900 mb-4">
              Everything you need to decide
            </h2>
            <p className="text-stone-500 max-w-xl mx-auto">
              Our AI analyzes hundreds of reviews and videos to surface what matters most.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="card-base group cursor-pointer animate-fade-in"
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-stone-900 transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-stone-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-medium text-stone-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it Works */}
        <section id="how-it-works" className="max-w-4xl mx-auto mt-32">
          <div className="text-center mb-16">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-stone-900 mb-4">
              How it works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Search', description: 'Enter any wedding venue name' },
              { step: '02', title: 'Analyze', description: 'AI processes reviews & videos' },
              { step: '03', title: 'Decide', description: 'Get your Venue Truth Report' }
            ].map((item, index) => (
              <div key={item.step} className="text-center animate-fade-in" style={{ animationDelay: `${(index + 1) * 150}ms` }}>
                <div className="w-16 h-16 bg-stone-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif text-2xl font-medium text-white">{item.step}</span>
                </div>
                <h3 className="font-medium text-stone-900 mb-2">{item.title}</h3>
                <p className="text-sm text-stone-500">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-stone-400 max-w-2xl mx-auto leading-relaxed">
              <span className="font-medium">Disclaimer:</span> WVC is a trust-first platform. We do not promote venues for payment or provide biased recommendations. Our insights are based on publicly available information, real reviews, and online content.
            </p>
          </div>
        </section>

        {/* Community Section */}
        <section id="community" className="max-w-4xl mx-auto mt-32">
          <div className="card-base p-8 sm:p-12 bg-gradient-to-br from-stone-50 to-amber-50/30 border-stone-200">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 rounded-full mb-6">
                <Users className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-medium text-stone-900 mb-4">
                WVC Community
              </h2>
              <p className="text-stone-600 max-w-2xl mx-auto leading-relaxed">
                This is where people talk about wedding venues in simple words. 
                You can ask doubts, read real experiences, and get honest opinions from others.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 mb-8 border border-stone-100">
              <p className="text-stone-600 text-center leading-relaxed">
                Before you book a venue, check what people are saying about things like 
                <span className="font-medium text-stone-800"> food</span>, 
                <span className="font-medium text-stone-800"> rooms</span>, 
                <span className="font-medium text-stone-800"> staff behavior</span>, 
                <span className="font-medium text-stone-800"> parking</span>, and 
                <span className="font-medium text-stone-800"> any hidden charges</span>.
              </p>
            </div>

            <div className="text-center">
              <Button
                onClick={() => navigate('/community')}
                className="btn-primary text-base px-8 py-6 h-auto"
                data-testid="join-community-btn"
              >
                <Users className="w-5 h-5 mr-2" />
                Join the Community
              </Button>
              <p className="text-sm text-stone-500 mt-4 flex items-center justify-center gap-2">
                <Shield className="w-4 h-4" />
                Login required to start or participate in discussions
              </p>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="max-w-4xl mx-auto mt-32 text-center">
          <div className="flex items-center justify-center gap-8 flex-wrap opacity-60">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-stone-400" />
              <span className="text-sm text-stone-500">Trusted by 1,000+ couples</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm text-stone-500">4.9/5 average rating</span>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-stone-900 rounded-full flex items-center justify-center">
              <span className="text-white font-serif text-xs font-bold">W</span>
            </div>
            <span className="font-serif text-sm text-stone-600">Wedding Venues Company</span>
          </div>
          <p className="text-xs text-stone-400">© 2026 Wedding Venues Company. All rights reserved.</p>
        </div>
      </footer>

      {/* Qualification Assistant */}
      {showQualification && (
        <QualificationAssistant
          onComplete={() => setShowQualification(false)}
          onSkip={() => setShowQualification(false)}
        />
      )}
    </div>
  );
};

export default HomePage;
